import React, { useMemo, useState, useEffect } from "react";
import Content from "../../components/Content";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useQuery } from "react-query";
import { fetchAllAlarms } from "../../api/AlarmApi";
import LatestAlarmMap from "../../components/dashboard/LatestAlarmMap";

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "Maj", "Jun",
  "Jul", "Aug", "Sep", "Okt", "Nov", "Dec",
];

const BAR_COLOR = "#8a1125";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value;
  return (
    <Paper
      sx={{
        p: 1.5,
        borderRadius: 2,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: "#5f6877" }}>
        Antal larm: <strong>{value}</strong>
      </Typography>
    </Paper>
  );
};

export const OverviewView = () => {
  const currentYear = new Date().getFullYear();
  const [organisationId, setOrganisationId] = useState(null);
  const [viewMode, setViewMode] = useState("stats");

  useEffect(() => {
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const { organisation_id } = JSON.parse(userData);
        setOrganisationId(organisation_id);
      }
    } catch (e) {
      console.error("Error getting organisation id:", e);
    }
  }, []);

  const { data: alarmsResponse, isLoading } = useQuery(
    ["allAlarms"],
    fetchAllAlarms
  );

  const orgAlarms = useMemo(() => {
    const alarmList = alarmsResponse?.data;
    if (!Array.isArray(alarmList) || !organisationId) return [];
    return alarmList.filter(
      (a) => String(a.organisation_id) === String(organisationId)
    );
  }, [alarmsResponse, organisationId]);

  const chartData = useMemo(() => {
    const yearAlarms = orgAlarms.filter(
      (a) => new Date(a.started).getFullYear() === currentYear
    );
    return MONTH_LABELS.map((month, monthIndex) => ({
      month,
      larm: yearAlarms.filter(
        (a) => new Date(a.started).getMonth() === monthIndex
      ).length,
    }));
  }, [orgAlarms, currentYear]);

  const latestAlarm = useMemo(() => {
    if (orgAlarms.length === 0) return null;
    return [...orgAlarms].sort(
      (a, b) => new Date(b.started).getTime() - new Date(a.started).getTime()
    )[0];
  }, [orgAlarms]);

  const handleModeChange = (_e, next) => {
    if (next) setViewMode(next);
  };

  const isStats = viewMode === "stats";

  return (
    <Content title="Översikt">
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          }}
        >
          <Box
            sx={{
              mb: 3,
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#1a1d23" }}
              >
                {isStats ? "Antal larm per månad" : "Senaste larm"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#5f6877" }}>
                {isStats
                  ? currentYear
                  : "Användarnas positioner vid larmtillfället"}
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleModeChange}
              size="small"
              sx={{
                background: "#f5f6f8",
                borderRadius: 2,
                p: 0.5,
                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: "8px !important",
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#5f6877",
                  px: 2,
                  gap: 0.75,
                },
                "& .MuiToggleButton-root.Mui-selected": {
                  background: "#ffffff",
                  color: "#8a1125",
                  boxShadow: "0 2px 8px rgba(16,50,74,0.12)",
                },
                "& .MuiToggleButton-root.Mui-selected:hover": {
                  background: "#ffffff",
                },
              }}
            >
              <ToggleButton value="stats">
                <BarChartRoundedIcon fontSize="small" />
                Statistik
              </ToggleButton>
              <ToggleButton value="map">
                <MapRoundedIcon fontSize="small" />
                Senaste larm
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {isStats ? (
            isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 400,
                }}
              >
                <CircularProgress />
              </Box>
            ) : chartData.every((d) => d.larm === 0) ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 400,
                }}
              >
                <Typography variant="body1" sx={{ color: "#5f6877" }}>
                  Inga larm registrerade för {currentYear}
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={450}>
                <BarChart
                  data={chartData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                  barCategoryGap="20%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#e8eaed"
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: "#5f6877", fontSize: 13 }}
                    axisLine={{ stroke: "#e8eaed" }}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: "#5f6877", fontSize: 13 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(0,0,0,0.04)" }}
                  />
                  <Bar
                    dataKey="larm"
                    name="Larm"
                    fill={BAR_COLOR}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={48}
                  />
                </BarChart>
              </ResponsiveContainer>
            )
          ) : (
            <LatestAlarmMap alarm={latestAlarm} />
          )}
        </Paper>
      </Box>
    </Content>
  );
};
